import { Observable, from, withLatestFrom, of, Subject, timer } from 'rxjs';
import { mergeMap, map, tap, debounce, distinctUntilChanged } from 'rxjs/operators';

/**
 ⏯ Begins a new subscription to the mapped Observable, only if there is no current subscription.
 * Any current Subscription is terminated.
 * Use when cancelability is important, like a video player tapped both to start and stop.
 *
 * @param workCreator The Observable-factory whose subscription is to be toggled
 * @example ```
 * const switch = new Subject();
 * const light = switch.pipe(toggleMap(() => new Observable(o => {
 *   console.log('connected: true');
 *   return () => console.log('connected: false');
 * }))).subscribe()
 *
 * switch.next(); // connected: true
 * switch.next(); // connected: false
 * ```
 */
function toggleMap(workCreator, mapper) {
    return function (source) {
        return new Observable((notify) => {
            let innerSub;
            return source.subscribe({
                next(trigger) {
                    if (!innerSub || innerSub.closed) {
                        innerSub = from(workCreator(trigger)).subscribe({
                            next: (inner) => {
                                const result = (mapper ? mapper(trigger, inner) : inner);
                                notify.next(result);
                            },
                            error: (e) => notify.error(e),
                        });
                    }
                    else {
                        innerSub.unsubscribe();
                    }
                },
                error(e) {
                    notify.error(e);
                },
                complete() {
                    notify.complete();
                },
            });
        });
    };
}

// @ts-nocheck
/**
 * Implements the Ember-Concurrency strategy [keepLatest](https://ember-concurrency.com/docs/task-concurrency#keepLatest).
 * Like queueing, but with a maximum queue size of 1. In the event of multiple conflicting events,
 * only the latest will trigger a handling, after the current handling is complete.
 * @param workCreator The Observable-factory whose latest subscription is to be enqueued
 * @param mapper A function to combine each emission of the togglable with the trigger itself, making it the new value of the togglable.
 */
function queueOnlyLatest(workCreator, mapper) {
    return function (source) {
        return new Observable((notify) => {
            let handlerSub;
            let work;
            // each new concurrent request overwrites this
            let nextWork;
            const nextViaMapper = ([result, trigger]) => {
                const _result = (mapper ? mapper(trigger, result) : result);
                notify.next(_result);
            };
            let workObserver = {
                complete() {
                    handlerSub = nextWork === null || nextWork === void 0 ? void 0 : nextWork.subscribe({
                        next: nextViaMapper,
                    });
                },
                next: nextViaMapper,
                error: (e) => notify.next(e),
            };
            function makeWork(trigger) {
                return from(workCreator(trigger)).pipe(withLatestFrom(of(trigger)));
            }
            return source.subscribe({
                next(trigger) {
                    if (!handlerSub || handlerSub.closed) {
                        // clear our queue
                        nextWork = null;
                        // start this work
                        work = makeWork(trigger);
                        handlerSub = work.subscribe(workObserver);
                    }
                    else {
                        // let the existing handlerSub call nextWork, which we'll populate
                        nextWork = makeWork(trigger);
                    }
                },
                error(e) {
                    notify.error(e);
                },
                complete() {
                    notify.complete(e);
                },
            });
        });
    };
}

/**
 * Defers all values and the completion until beyond the minimum time from now, if needed.
 * @param minimumMs
 * @returns
 */
function padToTime(minimumMs) {
    return function (source) {
        return new Observable((observer) => {
            const delayExpires = Date.now() + minimumMs;
            // Each maybe-delayed event gets put in here via .next(), and the observer gets the result
            const delays = new Subject();
            // Act on our delays immediately (mergeMap aka Immediate)
            // Every subscription begun gets added to 'cleanup', preserving cancelability
            const cleanup = delays.pipe(mergeMap((d) => d)).subscribe(observer);
            cleanup.add(source.subscribe({
                next(v) {
                    const timeLeft = Math.max(0, delayExpires - Date.now());
                    const delayedValue = timer(timeLeft).pipe(map(() => v));
                    delays.next(delayedValue);
                },
                complete() {
                    const timeLeft = Math.max(0, delayExpires - Date.now());
                    const delayedComplete = timer(timeLeft).pipe(tap(() => delays.complete()));
                    cleanup.add(delayedComplete.subscribe());
                },
            }));
            return cleanup;
        });
    };
}

/** Takes an Observable that may emit multiple times in a single frame/tick
 *  (like for a synchronous array), and returns one emitting only the final
 * emission of each frame. */
function debounceByPromise(obs) {
    return obs.pipe(debounce(() => Promise.resolve()), distinctUntilChanged());
}

export { debounceByPromise, padToTime, queueOnlyLatest, toggleMap };
//# sourceMappingURL=rxfx-operators.esm.js.map
